import { useState, useRef } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Alert, AlertDescription, AlertTitle } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Progress } from '@creanizer/ui'
import { AlertCircle, Calendar, Save, Globe, Users, Sword, Package } from 'lucide-react'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useStreamGenerator } from '../../../../hooks/useStreamGenerator'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { useEventStore } from '../../../../stores/eventStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useItemStore } from '../../../../stores/itemStore'
import { useEnemyStore } from '../../../../stores/enemyStore'
import { generateWorldContextText, generateCharacterContext, generateItemContext, generateEnemyContext } from '@creanizer/utils'
import type { EventInput, EventResult } from '@creanizer/types'
import { EventForm } from './EventForm'
import { EventFormSettings } from './EventFormSettings'
import { EventFormVariation } from './EventFormVariation'
import { EventResult as EventResultDisplay } from './EventResult'
import { SavedEventsList } from './SavedEventsList'

export function EventGenerator() {
  const { isApiKeySet } = useSettingsStore()
  const { events, addEvent, addBatchEvents } = useEventStore()
  const { currentWorld, savedWorlds } = useWorldBuilderStore()
  const { characters } = useCharacterStore()
  const { items } = useItemStore()
  const { enemies } = useEnemyStore()
  const generator = useGenerator('event')
  const streamGenerator = useStreamGenerator('event')
  const [activeTab, setActiveTab] = useState('generate')
  const [selectedWorldId, setSelectedWorldId] = useState<string>('')
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<string[]>([])
  const [characterRoles, setCharacterRoles] = useState<Record<string, string>>({})
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([])
  const [itemUsage, setItemUsage] = useState<Record<string, string>>({})
  const [selectedEnemyIds, setSelectedEnemyIds] = useState<string[]>([])
  const [enemyRoles, setEnemyRoles] = useState<Record<string, string>>({})
  const [useStreaming, setUseStreaming] = useState<boolean>(true)
  
  const [input, setInput] = useState<EventInput>({
    eventType: 'encounter',
    genre: 'fantasy',
    difficulty: 5,
    complexity: 'moderate',
    duration: 'medium',
    playerCount: '3-5人',
    requireChoices: false,
    numberOfChoices: 3,
    choiceComplexity: 'moderate',
    consequenceLevel: 'moderate',
    mode: 'single',
    numberOfEvents: 3,
    requireVariation: true,
    variationLevel: 8,
    creativityLevel: 8,
    // 世界観連携
    worldContext: currentWorld ? JSON.stringify({
      name: currentWorld.result.name,
      parameters: currentWorld.result.parameters,
      genre: currentWorld.result.genres
    }) : undefined
  })
  
  const [results, setResults] = useState<EventResult[]>([])
  const [error, setError] = useState<string>('')
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null)
  const resultRef = useRef<HTMLDivElement>(null)
  
  const handleGenerate = async () => {
    if (!isApiKeySet()) {
      setError('APIキーが設定されていません。設定ページから設定してください。')
      return
    }
    
    setError('')
    setResults([])
    setBatchProgress(null)
    
    // 関連データを入力に含める
    const finalInput: EventInput = {
      ...input,
      relatedCharacterContext: selectedCharacterIds.map(id => {
        const char = characters.find(c => c.id === id)
        return char ? generateCharacterContext(char) : ''
      }).filter(Boolean),
      characterRoles,
      relatedItemContext: selectedItemIds.map(id => {
        const item = items.find(i => i.id === id)
        return item ? generateItemContext(item) : ''
      }).filter(Boolean),
      itemUsage,
      relatedEnemyContext: selectedEnemyIds.map(id => {
        const enemy = enemies.find(e => e.id === id)
        return enemy ? generateEnemyContext(enemy) : ''
      }).filter(Boolean),
      enemyRoles
    }
    
    try {
      if (useStreaming) {
        await new Promise<void>((resolve, reject) => {
          streamGenerator.generateStream(finalInput, {
            onChunk: (chunk) => {
              console.log('Event chunk received:', chunk.length)
            },
            onProgress: (bytes) => {
              console.log('Event progress:', bytes)
            },
            onComplete: (resultData) => {
              if (resultData !== null && resultData !== undefined) {
                const rawResults = Array.isArray(resultData) ? resultData : [resultData]
                const validResults = rawResults.filter(event => 
                  event && event.basicInfo && event.basicInfo.name
                )
                
                if (validResults.length > 0) {
                  setResults(validResults as EventResult[])
                  
                  // ストアに保存
                  if (input.mode === 'single' && validResults.length === 1) {
                    addEvent({
                      title: validResults[0].basicInfo.name,
                      input: finalInput,
                      result: validResults[0]
                    })
                  } else {
                    const savedEvents = validResults.map(result => ({
                      title: result.basicInfo.name,
                      input: finalInput,
                      result
                    }))
                    addBatchEvents(savedEvents)
                  }
                  
                  setTimeout(() => {
                    resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }, 100)
                }
                resolve()
              } else {
                reject(new Error('イベントの生成に失敗しました'))
              }
            },
            onError: (error) => {
              console.error('Event generation error:', error)
              setError(error.message || 'イベントの生成に失敗しました')
              reject(error)
            }
          })
        })
      } else {
        const response = await generator.mutateAsync(finalInput)
        const resultData = response.result || response
        
        if (resultData) {
          const rawResults = Array.isArray(resultData) ? resultData : [resultData]
          const validResults = rawResults.filter(event => 
            event && event.basicInfo && event.basicInfo.name
          )
          
          if (validResults.length > 0) {
            setResults(validResults as EventResult[])
            
            // ストアに保存
            if (input.mode === 'single' && validResults.length === 1) {
              addEvent({
                title: validResults[0].basicInfo.name,
                input: finalInput,
                result: validResults[0]
              })
            } else {
              const savedEvents = validResults.map(result => ({
                title: result.basicInfo.name,
                input: finalInput,
                result
              }))
              addBatchEvents(savedEvents)
            }
            
            setTimeout(() => {
              resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }, 100)
          } else {
            setError('有効なイベントが生成されませんでした')
          }
        }
      }
    } catch (error: any) {
      console.error('Event generation error:', error)
      setError(error.message || 'イベントの生成中にエラーが発生しました')
    }
  }
  
  const handleWorldSelect = (worldId: string) => {
    setSelectedWorldId(worldId)
    const world = savedWorlds.find(w => w.id === worldId) || currentWorld
    if (world) {
      setInput(prev => ({
        ...prev,
        worldContext: generateWorldContextText(world.result)
      }))
    }
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Calendar className="w-6 h-6" />
        <h1 className="text-2xl font-bold">イベントジェネレータ</h1>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 w-[400px]">
          <TabsTrigger value="generate">イベント生成</TabsTrigger>
          <TabsTrigger value="saved">
            保存済み ({events.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="generate" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              {/* 世界観選択 */}
              {(currentWorld || savedWorlds.length > 0) && (
                <div className="space-y-2">
                  <Label htmlFor="world-select">
                    <Globe className="inline w-4 h-4 mr-1" />
                    世界観を選択
                  </Label>
                  <Select value={selectedWorldId} onValueChange={handleWorldSelect}>
                    <SelectTrigger id="world-select">
                      <SelectValue placeholder="世界観を選択（オプション）" />
                    </SelectTrigger>
                    <SelectContent>
                      {currentWorld && (
                        <SelectItem value="current">現在の世界観: {currentWorld.result.name}</SelectItem>
                      )}
                      {savedWorlds.map(world => (
                        <SelectItem key={world.id} value={world.id}>
                          {world.result.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* 関連キャラクター選択 */}
              {characters.length > 0 && (
                <div className="space-y-2">
                  <Label>
                    <Users className="inline w-4 h-4 mr-1" />
                    関連キャラクター（複数可）
                  </Label>
                  <div className="space-y-2 max-h-32 overflow-y-auto border rounded p-2">
                    {characters.map(char => (
                      <label key={char.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedCharacterIds.includes(char.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedCharacterIds([...selectedCharacterIds, char.id])
                            } else {
                              setSelectedCharacterIds(selectedCharacterIds.filter(id => id !== char.id))
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm">{char.result.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              {/* 関連アイテム選択 */}
              {items.length > 0 && (
                <div className="space-y-2">
                  <Label>
                    <Package className="inline w-4 h-4 mr-1" />
                    関連アイテム（複数可）
                  </Label>
                  <div className="space-y-2 max-h-32 overflow-y-auto border rounded p-2">
                    {items.map(item => (
                      <label key={item.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedItemIds.includes(item.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedItemIds([...selectedItemIds, item.id])
                            } else {
                              setSelectedItemIds(selectedItemIds.filter(id => id !== item.id))
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm">{item.result.basicInfo.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              {/* 関連エネミー選択 */}
              {enemies.length > 0 && (
                <div className="space-y-2">
                  <Label>
                    <Sword className="inline w-4 h-4 mr-1" />
                    関連エネミー（複数可）
                  </Label>
                  <div className="space-y-2 max-h-32 overflow-y-auto border rounded p-2">
                    {enemies.map(enemy => (
                      <label key={enemy.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedEnemyIds.includes(enemy.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEnemyIds([...selectedEnemyIds, enemy.id])
                            } else {
                              setSelectedEnemyIds(selectedEnemyIds.filter(id => id !== enemy.id))
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm">{enemy.result.basicInfo.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="space-y-4">
              <EventForm input={input} onChange={setInput} />
              <EventFormVariation input={input} onChange={setInput} />
              <EventFormSettings 
                input={input} 
                onChange={setInput}
                useStreaming={useStreaming}
                setUseStreaming={setUseStreaming}
              />
            </div>
          </div>
          
          <Button
            onClick={handleGenerate}
            disabled={generator.isPending || streamGenerator.isStreaming}
            className="w-full"
          >
            {generator.isPending || streamGenerator.isStreaming ? (
              <>生成中...</>
            ) : (
              <>イベントを生成</>  
            )}
          </Button>
          
          {batchProgress && (
            <Progress value={(batchProgress.current / batchProgress.total) * 100} />
          )}
          
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>エラー</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {results.length > 0 && (
            <div ref={resultRef}>
              <EventResultDisplay results={results} mode={input.mode || 'single'} />
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="saved">
          <SavedEventsList />
        </TabsContent>
      </Tabs>
    </div>
  )
}
